import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    Color,
    LifecycleAware,
    Modal,
    Orientation,
    forLifeOf,
    ClassNames,
    ignoreNullish
} from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ManageSupplierService } from '../../manage-supplier.service';
import { GetSupplierRelationshipDetailsResponse } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-supplier/get-supplier-relationship-details';
import { SetBuyerPaidTransferFeesService } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-supplier/set-buyer-paid-transfer-fees';
import { SaveBuyerPaidTransferFeesModalComponent } from '../../../modals/save-buyer-paid-transfer-fees-modal.component';

@LifecycleAware()
@Component({
    selector: 'supplier-implementation-transfer-fees',
    styleUrls: ['./supplier-implementation-transfer-fees.component.scss'],
    template: `
        <card [headerTitle]="transferFeesModalHeaderI18n" [style.marginBottom]="'3rem'">
            <div card-body *ngIf="!overrideBuyerFeesFormGroup" class="implementation-card-body">
                <lsq-circle-loader></lsq-circle-loader>
            </div>
            <div card-body *ngIf="!!overrideBuyerFeesFormGroup" class="implementation-card-body">
                <div class="fees-structure-controls">
                    <form class="control-inputs" [formGroup]="overrideBuyerFeesFormGroup">
                        <div class="control-row" [style.marginBottom]="'1.8rem'">
                            <lsq-switch
                                [labelOrientation]="orientation.Right"
                                formControlName="overridingBuyerPaidTransferFees"
                                name="overridingBuyerPaidTransferFees"
                                [group]="overrideBuyerFeesFormGroup"
                                [style.marginRight]="'1.5rem'"
                                id="overridingBuyerPaidTransferFees"
                            >
                            </lsq-switch>
                            <div [style.width]="'100%'">
                                <lsq-label
                                    [label]="transferFeesI18n"
                                    [subLabel]="transferFeeDescriptionI18n"
                                ></lsq-label>
                            </div>
                        </div>
                        <div
                            class="control-row"
                            [style.paddingLeft]="'5rem'"
                            [style.marginBottom]="'1.8rem'"
                        >
                            <lsq-switch
                                [labelOrientation]="orientation.Right"
                                formControlName="buyerPaidTransferFees"
                                name="buyerPaidTransferFees"
                                id="buyerPaidTransferFees"
                                [group]="overrideBuyerFeesFormGroup"
                                [style.marginRight]="'1.5rem'"
                                [disabled]="
                                    !overrideBuyerFeesFormGroup.get(
                                        'overridingBuyerPaidTransferFees'
                                    ).value
                                "
                            ></lsq-switch>
                            <div [style.width]="'100%'">
                                <div class="control-inputs">
                                    <div [style.width]="'100%'">
                                        <lsq-label
                                            [label]="buyerPaidI18n"
                                            [subLabel]="buyerPaidDescriptionI18n"
                                        ></lsq-label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <lsq-button id="saveTransferFeesButton" (buttonClick)="openSaveModal()">
                            Save
                        </lsq-button>
                    </form>
                </div>
            </div>
        </card>
    `
})
export class ManageSupplierImplementationTransferFeesComponent implements OnInit, OnDestroy {
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly orientation = Orientation;

    public relationship: GetSupplierRelationshipDetailsResponse;

    public buyerPaidFeesTransferFormSubs: Subscription;
    public overridingBuyerTransferSubs: Subscription;

    constructor(
        private _manageSupplierService: ManageSupplierService,
        private _modalService: ModalService,
        private _i18n: I18n,
        private _setBuyerPaidTransferFeesService: SetBuyerPaidTransferFeesService
    ) {}

    public ngOnInit(): void {
        this._manageSupplierService.currentSupplierRelationship$
            .pipe(forLifeOf(this), ignoreNullish())
            .subscribe((relationship) => {
                this.relationship = relationship;
                this._setupForm();
            });
    }

    public overrideBuyerFeesFormGroup: FormGroup = new FormGroup({
        overridingBuyerPaidTransferFees: new FormControl(null),
        buyerPaidTransferFees: new FormControl(null)
    });

    private _setupForm(): void {
        const relationshipBuyerPaidTransferFees =
            this.relationship.relationshipBuyerPaidTransferFees ??
            this.relationship.buyerPaidTransferFees;

        this.overrideBuyerFeesFormGroup.reset({
            buyerPaidTransferFees: relationshipBuyerPaidTransferFees ?? 0,
            overridingBuyerPaidTransferFees:
                this.relationship.relationshipBuyerPaidTransferFees !== null
        });

        this.overrideBuyerFeesFormGroup.updateValueAndValidity();
        this.overrideBuyerFeesFormGroup.markAllAsTouched();

        this._manageSupplierService.defaultSettings.buyerPaidDeliveryFees = this.formatValue(
            this.relationship.buyerPaidTransferFees
        );

        this._manageSupplierService.currentSettings.buyerPaidDeliveryFees = this._manageSupplierService.currentOverridingSettings.buyerPaidDeliveryFees = this.formatValue(
            relationshipBuyerPaidTransferFees
        );

        this.buyerPaidFeesTransferFormSubs = this.overrideBuyerFeesFormGroup.valueChanges
            .pipe(forLifeOf(this))
            .subscribe((form) => {
                this._manageSupplierService.currentOverridingSettings.buyerPaidDeliveryFees = this.formatValue(
                    form.buyerPaidTransferFees
                );
            });

        this.overridingBuyerTransferSubs = this.overrideBuyerFeesFormGroup
            .get('overridingBuyerPaidTransferFees')
            .valueChanges.pipe(forLifeOf(this))
            .subscribe((value) => {
                if (!value && this.relationship.buyerPaidTransferFees) {
                    this.overrideBuyerFeesFormGroup
                        .get('buyerPaidTransferFees')
                        .patchValue(this.relationship.buyerPaidTransferFees);
                    this.overrideBuyerFeesFormGroup.updateValueAndValidity();
                } else if (value) {
                    this.overrideBuyerFeesFormGroup.get('buyerPaidTransferFees').patchValue(false);
                    this.overrideBuyerFeesFormGroup.updateValueAndValidity();
                } else {
                    this.overrideBuyerFeesFormGroup.get('buyerPaidTransferFees').patchValue(null);
                    this.overrideBuyerFeesFormGroup.updateValueAndValidity();
                }
            });
    }

    public ngOnDestroy(): void {
        this.buyerPaidFeesTransferFormSubs?.unsubscribe();
        this.overridingBuyerTransferSubs?.unsubscribe();
    }

    public openSaveModal() {
        this.overrideBuyerFeesFormGroup.updateValueAndValidity();
        this.overrideBuyerFeesFormGroup.markAllAsTouched();

        if (this.overrideBuyerFeesFormGroup.valid) {
            const isSubmitting$ = new BehaviorSubject<boolean>(false);
            const buyerPaidTransferFees = this.overrideBuyerFeesFormGroup.get(
                'buyerPaidTransferFees'
            ).value;
            const overridingBuyerPaidTransferFees = this.overrideBuyerFeesFormGroup.get(
                'overridingBuyerPaidTransferFees'
            ).value;

            this._modalService.showModal(
                new Modal({
                    id: 'save-transfer-fees',
                    title: this.transferFeesModalHeaderI18n,
                    showFooter: true,
                    showOkButton: true,
                    bodyComponent: SaveBuyerPaidTransferFeesModalComponent,
                    data: {
                        paidTransfer: this.formatValue(buyerPaidTransferFees)
                    },
                    okButtonText: this.saveSettingI18n,
                    okButtonSize: ClassNames.Block,
                    showCancelButton: true,
                    cancelButtonText: this.cancelI18n,
                    isSubmitting$,
                    onOkClick: async () => {
                        isSubmitting$.next(true);
                        await this._setBuyerPaidTransferFeesService.executeOnce({
                            relationshipKey: this.relationship.relationshipKey,
                            overridingPaidTransferFees: overridingBuyerPaidTransferFees,
                            paidTransferFees: buyerPaidTransferFees
                        });
                        isSubmitting$.next(false);
                        this._modalService.closeModal();
                        await this._manageSupplierService.loadRelationship(
                            this.relationship.relationshipKey
                        );
                    },
                    onBackdropClick: async () => {
                        this._modalService.closeModal();
                    },
                    onCancelClick: async () => {
                        this._modalService.closeModal();
                    }
                })
            );
        }
    }

    public formatValue(value: any): string {
        if (value === null || typeof value === 'undefined') {
            return '--';
        }
        return value ? 'Buyer Paid' : 'Supplier Paid';
    }

    public readonly transferFeesModalHeaderI18n = this._i18n({
        value: 'Buyer Paid Transfer Fees',
        description: 'Modal header'
    });

    public readonly saveSettingI18n = this._i18n({
        value: 'Save Settings',
        description: 'Save modal button'
    });
    public readonly cancelI18n = this._i18n({
        value: 'Cancel',
        description: 'Modal cancel button'
    });

    public readonly transferFeesHeaderI18n = this._i18n({
        value: 'Buyer Paid Transfer Fees',
        description: 'Card header'
    });

    public readonly transferFeesI18n = this._i18n({
        value: 'Buyer Paid Transfer Fees',
        description: 'Overriding transfer value field'
    });

    public readonly transferFeeDescriptionI18n = this._i18n({
        value: 'Set an override of the Buyer account-level settings for this fee type.',
        description: 'Overriding transfer value description'
    });

    public readonly buyerPaidI18n = this._i18n({
        value: 'Buyer Paid',
        description: 'Override transfer value field'
    });

    public readonly buyerPaidDescriptionI18n = this._i18n({
        value: 'Buyer is responsible for paying all early payment transfer fees.',
        description: 'Override transfer value description'
    });
}
